import * as act from '@/store/sign.actions';
import { actions as storeActions } from '@/models/store.actions';
import { DepositSignAuthType, CloudProvider } from '@/sign/models/sign-constants';

class Certificate {
  uid: string;
  fileName: string;
  content: string;

  constructor(uid: string, fileName: string, content: string) {
    this.uid = uid;
    this.fileName = fileName;
    this.content = content;
  }
}

const state = {
  selectedCN: null,
  autoSelectCN: false,
  selectedTab: 0,
  selectedCNSeal: null,
  autoSelectCNSeal: false,
  selectedTabSeal: 0,
  certificates: {},
  selectedCloudProvider: '',
  cloudProviders: {},
  selectedCloudProviderSeal: '',
  cloudProvidersSeal: {},
  selectedDevice: null,
  showAllDevices: false,
  selectedDeviceSeal: null,
  showAllDevicesSeal: false
};

const actions = {};

const mutations = {
  [act.MUT_SELECTEDCN](state: any, { selectedCN }): void {
    state.selectedCN = selectedCN;
  },
  [act.MUT_AUTOSELECTCN](state: any, { autoSelectCN }): void {
    state.autoSelectCN = autoSelectCN;
  },
  [act.MUT_SELECTEDTAB](state: any, { selectedTab }): void {
    state.selectedTab = selectedTab;
  },
  [act.MUT_SELECTEDCNSEAL](state: any, { selectedCN }): void {
    state.selectedCNSeal = selectedCN;
  },
  [act.MUT_AUTOSELECTCNSEAL](state: any, { autoSelectCN }): void {
    state.autoSelectCNSeal = autoSelectCN;
  },
  [act.MUT_SELECTEDTABSEAL](state: any, { selectedTab }): void {
    state.selectedTabSeal = selectedTab;
  },
  [act.MUT_CLOUD](state: any, { selectedCloud }): void {
    state.selectedCloudProvider = selectedCloud;
  },
  [act.MUT_CLOUD_SEAL](state: any, { selectedCloud }): void {
    state.selectedCloudProviderSeal = selectedCloud;
  },
  [act.MUT_CLOUD_PROVIDER](state: any, { selectedCloud, cloudProvider }): void {
    let savedCloudProvider = state.cloudProviders[selectedCloud];
    state.cloudProviders[selectedCloud] = Object.assign(savedCloudProvider || {}, cloudProvider);
  },
  [act.MUT_CLOUD_PROVIDER_SEAL](state: any, { selectedCloud, cloudProvider }): void {
    let savedCloudProvider = state.cloudProvidersSeal[selectedCloud];
    state.cloudProvidersSeal[selectedCloud] = Object.assign(savedCloudProvider || {}, cloudProvider);
  },
  [act.MUT_ADD_USER_CERT](state: any, { email, cert }: { email: string, cert: Certificate }): void {
    if (cert && cert.uid) {
      const user = email || '';
      if (!(user in state.certificates)) {
        state.certificates = Object.assign({}, state.certificates, { [user]: [] });
      }
      if (!state.certificates[user].some(c => c.uid === cert.uid)) {
        state.certificates[user].push(cert);
      }
    }
  },
  [act.MUT_DEL_USER_CERT](state: any, { email, certUid }: { email: string, certUid: string }): void {
    const user = email || '';
    if (state.certificates && state.certificates[user]) {
      const certIndex = state.certificates[user].findIndex(c => c.uid === certUid);
      if (certIndex > -1) {
        state.certificates[user].splice(certIndex, 1);
      }
    }
  },
  [act.MUT_CLEAR_USER_CERTS](state: any, { email }: { email: string }): void {
    const user = email || '';
    if (state.certificates && state.certificates[user]) {
      state.certificates[user].splice(0);
    }
  },
  [act.MUT_SHOWALLDEVICES](state: any, { showAllDevices }): void {
    state.showAllDevices = showAllDevices;
  },
  [act.MUT_SHOWALLDEVICES_SEAL](state: any, { showAllDevices }): void {
    state.showAllDevicesSeal = showAllDevices;
  },
  [act.MUT_SELECTEDDEVICE](state: any, { selectedDevice }): void {
    state.selectedDevice = selectedDevice;
  },
  [act.MUT_SELECTEDDEVICE_SEAL](state: any, { selectedDevice }): void {
    state.selectedDeviceSeal = selectedDevice;
  }
};

const getters = {
  [act.GET_SELECTEDCN]: state => {
    return state.selectedCN;
  },
  [act.GET_AUTOSELECTCN]: state => {
    return state.autoSelectCN;
  },
  [act.GET_SELECTEDTAB]: state => {
    return state.selectedTab;
  },
  [act.GET_SELECTEDCNSEAL]: state => {
    return state.selectedCNSeal;
  },
  [act.GET_AUTOSELECTCNSEAL]: state => {
    return state.autoSelectCNSeal;
  },
  [act.GET_SELECTEDTABSEAL]: state => {
    return state.selectedTabSeal;
  },
  [act.GET_CLOUD]: state => {
    return {
      selectedCloud: state.selectedCloudProvider
    };
  },
  [act.GET_CLOUD_SEAL]: state => {
    return {
      selectedCloud: state.selectedCloudProviderSeal
    };
  },
  [act.GET_CLOUD_PROVIDER]: state => selectedCloudProvider => {
    return state.cloudProviders[selectedCloudProvider] || {};
  },
  [act.GET_CLOUD_PROVIDER_SEAL]: state => selectedCloudProvider => {
    return state.cloudProvidersSeal[selectedCloudProvider] || {};
  },
  [act.GET_USER_CERT]: state => email => {
    return (state.certificates ? state.certificates[email] : null) || [];
  },
  [act.GET_SHOWALLDEVICES]: (state: any) => state.showAllDevices,
  [act.GET_SHOWALLDEVICES_SEAL]: (state: any) => state.showAllDevicesSeal,
  [act.GET_SELECTEDDEVICE]: (state: any) => state.selectedDevice,
  [act.GET_SELECTEDDEVICE_SEAL]: (state: any) => state.selectedDeviceSeal
};

export const sign = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};

const oldCloudProvidersMap = {
  1: CloudProvider.DepositSign,
  2: CloudProvider.DPDiya,
  3: CloudProvider.PrivatSmartId,
  4: CloudProvider.Ukey
};

export function updateSignCloudProvidersPlugin(store: any) {
  let { sign } = store.state;

  if ('cloudClientId' in sign) {
    sign.selectedCloudProvider = oldCloudProvidersMap[sign.selectedCloud];
    delete sign.selectedCloud;

    let cloudClientId = sign.cloudClientId;
    delete sign.cloudClientId;

    let selectedCloud = sign.selectedCloudProvider;
    let cloudProvider = {
      authType: DepositSignAuthType.TwoFactor,
      clientId: cloudClientId,
      login: ''
    };
    store.commit(storeActions.SIGN_MUT_CLOUD_PROVIDER, { selectedCloud, cloudProvider });
  }
}
